import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Spinner,
  Table,
  Alert,
} from "react-bootstrap";
import { FaSyncAlt } from "react-icons/fa";
import { FaSearch } from "react-icons/fa";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";

const MassonTraining = () => {
  const [trainings, setTrainings] = useState([]);
  const [filteredTrainings, setFilteredTrainings] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [talukas, setTalukas] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedTaluka, setSelectedTaluka] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  const API = process.env.REACT_APP_API_URL;

  // ✅ Fetch Data
  useEffect(() => {
    const fetchTrainings = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API}/massontraining/masontraining`);
        if (!response.ok) throw new Error("Failed to fetch mason trainings");
        const data = await response.json();
        setTrainings(data.data || []);
        setFilteredTrainings(data.data || []);
        const uniqueDistricts = [
          ...new Set(data.data.map((item) => item.district_name)),
        ];
        setDistricts(uniqueDistricts);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchTrainings();
  }, [API]);

  // ✅ Populate Taluka when District changes
  useEffect(() => {
    if (selectedDistrict) {
      const talukasInDistrict = [
        ...new Set(
          trainings
            .filter((item) => item.district_name === selectedDistrict)
            .map((item) => item.taluka_name)
        ),
      ];
      setTalukas(talukasInDistrict);
    } else {
      setTalukas([]);
    }
  }, [selectedDistrict, trainings]);

  // ✅ Filter Data
  useEffect(() => {
    let filtered = trainings;
    if (selectedDistrict)
      filtered = filtered.filter(
        (item) => item.district_name === selectedDistrict
      );
    if (selectedTaluka)
      filtered = filtered.filter((item) => item.taluka_name === selectedTaluka);
    if (searchTerm)
      filtered = filtered.filter((item) =>
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    setFilteredTrainings(filtered);
  }, [selectedDistrict, selectedTaluka, trainings, searchTerm]);

  // ✅ Refresh Filters
  const handleRefresh = () => {
    setSelectedDistrict("");
    setSelectedTaluka("");
    setSearchTerm("");
    setFilteredTrainings(trainings);
  };

  // ✅ Export to Excel
  const exportToExcel = (data, filename) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Mason Trainings");
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  // ✅ Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Mason Training Report", 14, 16);
    doc.autoTable({
      head: [
        ["Sr. No", "District", "Total Trained", "Completed", "Assessment", "Passed"],
      ],
      body: filteredTrainings.map((t, idx) => [
        idx + 1,
        t.district_name,
        t.yekun_prashikshan_sankya || "0",
        t.prashikshan_purn_zalelya_gavandi_sankya || "0",
        t.assessment_gavandi_sankya || "0",
        t.utirn_gavandi_sankya || "0",
      ]),
    });
    doc.save("MasonTraining.pdf");
  };

  // ✅ Totals
  const totals = filteredTrainings.reduce(
    (acc, t) => {
      acc.totalTrainings += parseInt(t.yekun_prashikshan_sankya) || 0;
      acc.completedTrainings +=
        parseInt(t.prashikshan_purn_zalelya_gavandi_sankya) || 0;
      acc.assessmentCount += parseInt(t.assessment_gavandi_sankya) || 0;
      acc.passedCount += parseInt(t.utirn_gavandi_sankya) || 0;
      return acc;
    },
    {
      totalTrainings: 0,
      completedTrainings: 0,
      assessmentCount: 0,
      passedCount: 0,
    }
  );

  return (
    <Container fluid>
      <Row>
        {/* LEFT SIDEBAR (Copied from ConvergenceOne style) */}
        <Col md={2} className="p-4">
          <Button
            variant="secondary"
            onClick={() => navigate("/")}
            className="mb-3 w-100 fw-semibold"
            style={{ height: "42px" }}
          >
            Back
          </Button>

          <div
            className="p-3 mt-2 shadow-sm rounded"
            style={{
              backgroundColor: "#EAF0F9",
              borderRadius: "8px",
            }}
          >
            {/* Filters Header + Refresh */}
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h5
                style={{
                  fontWeight: "bold",
                  color: "#1F3C88",
                  margin: 0,
                }}
              >
                Filters
              </h5>
              <div
                style={{
                  backgroundColor: "#EAF2FF",
                  borderRadius: "50%",
                  padding: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "0.3s",
                }}
                onClick={handleRefresh}
                title="Refresh"
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "rotate(90deg)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "rotate(0deg)")
                }
              >
                <FaSyncAlt size={16} style={{ color: "#1F3C88" }} />
              </div>
            </div>

            <Form>
              <Form.Group controlId="districtSelect" className="mb-3">
                <Form.Label>District</Form.Label>
                <Form.Select
                  value={selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value)}
                >
                  <option value="">All Districts</option>
                  {districts.map((d, idx) => (
                    <option key={idx} value={d}>
                      {d}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group controlId="talukaSelect" className="mb-3">
                <Form.Label>Taluka</Form.Label>
                <Form.Select
                  value={selectedTaluka}
                  onChange={(e) => setSelectedTaluka(e.target.value)}
                  disabled={!selectedDistrict}
                >
                  <option value="">All Talukas</option>
                  {talukas.map((t, idx) => (
                    <option key={idx} value={t}>
                      {t}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Form>
          </div>
        </Col>

        {/* RIGHT MAIN PANEL */}
        <Col md={10} className="p-4">
          <div
            className="d-flex align-items-center justify-content-between mb-4"
            style={{ flexWrap: "wrap", gap: "10px" }}
          >
            <h5
              className="fw-bold mb-0"
              style={{
                textAlign: "left",
                fontSize: "18px",
                color: "#1F3C88",
              }}
            >
              Mason Training Report
            </h5>

            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <Form.Group controlId="searchBox" style={{ width: "250px" }}>
                <Form.Control
                  type="text"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    borderRadius: "6px",
                    height: "38px",
                    fontSize: "14px",
                    border: "1px solid #ced4da",
                  }}
                />
              </Form.Group>

              <Button
                onClick={() => exportToExcel(filteredTrainings, "MasonTraining")}
                style={{
                  backgroundColor: "#4da6ff",
                  borderColor: "#4da6ff",
                  color: "#fff",
                  height: "42px",
                  fontSize: "14px",
                  minWidth: "130px",
                  borderRadius: "6px",
                }}
              >
                Export to Excel
              </Button>

              {/* <Button
                onClick={exportToPDF}
                style={{
                  backgroundColor: "#66cc66",
                  borderColor: "#66cc66",
                  color: "#fff",
                  height: "42px",
                  fontSize: "14px",
                  minWidth: "130px",
                  borderRadius: "6px",
                }}
              >
                Export to PDF
              </Button> */}
            </div>
          </div>

          {/* TABLE SECTION */}
          {loading ? (
            <Spinner animation="border" className="d-block mx-auto" />
          ) : error ? (
            <Alert variant="danger">{error}</Alert>
          ) : (
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              <Table striped bordered hover responsive>
                <thead
                  style={{
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#f8f9fa",
                    textAlign: "center",
                    zIndex: 0,
                  }}
                >
                  <tr>
                    <th>Sr. No</th>
                    <th>District</th>
                    <th>Total Trained</th>
                    <th>Completed</th>
                    <th>Assessment</th>
                    <th>Passed</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTrainings.length > 0 ? (
                    <>
                      {filteredTrainings.map((training, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{training.district_name}</td>
                          <td>{training.yekun_prashikshan_sankya || "0"}</td>
                          <td>
                            {training.prashikshan_purn_zalelya_gavandi_sankya ||
                              "0"}
                          </td>
                          <td>{training.assessment_gavandi_sankya || "0"}</td>
                          <td>{training.utirn_gavandi_sankya || "0"}</td>
                        </tr>
                      ))}
                      <tr style={{ backgroundColor: "#eef5ff" }}>
                        <td colSpan="2">
                          <strong>Total</strong>
                        </td>
                        <td>
                          <strong>{totals.totalTrainings}</strong>
                        </td>
                        <td>
                          <strong>{totals.completedTrainings}</strong>
                        </td>
                        <td>
                          <strong>{totals.assessmentCount}</strong>
                        </td>
                        <td>
                          <strong>{totals.passedCount}</strong>
                        </td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan="6" className="text-center">
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default MassonTraining;
